import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { IDevice } from "lavva.exalushome/build/js/Services/Devices/IDevice";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { LightTransitionTime } from "../../Dimmer/Rdp21ConfigParameters";
import { GetChannelParamsRequest } from "../DataRequest";
import { GlobalTimeParameters } from "../OnOffSwitchConfigurations/OnOffSwitchParameters";
import { OnOffSwitchService, SwitchConfigurationErrorCode } from "../OnOffSwitchConfigurations/OnOffSwitchService";
import { Time as TimeParam } from "../OnOffSwitchConfigurations/OnOffSwitchParameters";
import { LedControllersLightTransitionTime } from "../../LedController/SlrConfigParameters";

export class LedControllerService extends OnOffSwitchService {
    public static readonly ServiceName: string = "LedControllerService";

    constructor() {
        super();
    }

    GetServiceName(): string {
        return LedControllerService.ServiceName;
    }

    async GetDimmingAndBrighteningTimeAsync(device: IDevice, channel: number): Promise<LedControllersLightTransitionTime | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get dimming and brightening time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<DimmerSettingsParameters>(new GetDimmingAndBrighteningTimeRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming and brightening time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming and brightening time - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get dimming and brightening time - no data in response.`);

            return result.Data.State as LedControllersLightTransitionTime;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get dimming and brightening time - unknown error.`);
        }
    }

    async SetDimmingAndBrighteningTimeAsync(device: IDevice, channel: number, time: LedControllersLightTransitionTime): Promise<Status> {
        try {
            const request = new DimmerSettingsParameters();
            request.DeviceGuid = device.Guid;
            request.Channel = channel;
            request.State = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetDimmingAndBrighteningTimeRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set dimming and brightening time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set dimming and brightening time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetTurnOffTimeAsync(device: IDevice, channel: number): Promise<GlobalTimeParameters | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get turn off time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<GlobalTimeParametersFrame>(new GetTurnOffTimeRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn off time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn off time - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get turn off time - no data in response.`);

            const data = new GlobalTimeParameters();
            data.Channel = result.Data.Channel;
            data.Time = this.ParseNumberToTime(result.Data.Time);

            return data;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get turn off time - unknown error.`);
        }
    }

    async SetTurnOffTimeAsync(device: IDevice, params: GlobalTimeParameters): Promise<Status> {
        try {
            const time = params.Time.Hours * 3600 + params.Time.Minutes * 60 + params.Time.Seconds;

            if (time > 240) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to turn off time - time must be in range between 0 and 240 seconds.`);
                return Status.WrongData;
            }

            const request = new GlobalTimeParametersFrame();
            request.Channel = params.Channel;
            request.DeviceGuid = device.Guid;
            request.Time = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetTurnOffTimeRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to turn off time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to turn off time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetFloatingTimeAsync(device: IDevice, channel: number): Promise<GlobalTimeParameters | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot floating time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<GlobalTimeParametersFrame>(new GetFloatingTimeRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot floating time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot floating time - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot floating time - no data in response.`);

            const data = new GlobalTimeParameters();
            data.Channel = result.Data.Channel;
            data.Time = this.ParseNumberToTime(result.Data.Time);

            return data;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot floating time - unknown error.`);
        }
    }

    async SetFloatingTimeAsync(device: IDevice, params: GlobalTimeParameters): Promise<Status> {
        try {
            const time = params.Time.Hours * 3600 + params.Time.Minutes * 60 + params.Time.Seconds;

            if (time > 64800) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set floating time - time must be in range between 1s to 18 hours.`);
                return Status.WrongData;
            }

            const request = new GlobalTimeParametersFrame();
            request.Channel = params.Channel;
            request.DeviceGuid = device.Guid;
            request.Time = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetFloatingTimeRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set floating time - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set floating time, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetTurnOnOffTimeActivationAsync(device: IDevice): Promise<boolean | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new GetTurnOnOffTimeActivation(device.Guid), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on/off time activation - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on/off time activation - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get turn on/off time activation - no data in response.`);

            return result.Data;
        } 
        catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on/off time activation - unknown error.`);
        }
    }

    async SetTurnOnOffTimeActivationAsync(device: IDevice, activation: boolean): Promise<Status> {
        try {
            const request = new ActivationOnOffTimes();
            request.DeviceGuid = device.Guid;
            request.Active = activation;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetTurnOnOffTimeActivation(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on/off time activation - unknow error.`);
                return Status.FatalError;
            }

            return result.Status;
        } catch (error) {
            this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on/off time activation, error: ${error}`);
            return Status.FatalError;
        }
    }
}

//BrighteningAndDimmingTime
class DimmerSettingsParameters {
    Channel: number = 0;
    DeviceGuid: string = "";
    State: number = 0;
    StateOptional: number = 0;
}

class GetDimmingAndBrighteningTimeRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/brightening/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetDimmingAndBrighteningTimeRequest extends DataFrame<DimmerSettingsParameters> {
    constructor(data: DimmerSettingsParameters) {
        super();
        this.Resource = "/extalife/device/parameters/brightening/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//Global time
class Time extends TimeParam {
    public set Raw(value: number) {
        this._raw = value;
    }
}

class GlobalTimeParametersFrame {
    Channel: number = 0;
    DeviceGuid: string = "";
    Time: number = 0;
}

//Turn on/off time activation
class ActivationOnOffTimes {
    DeviceGuid: string = "";
    Active: boolean = false;
}


//TrunOffTime
class GetTurnOffTimeRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/device/off/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetTurnOffTimeRequest extends DataFrame<GlobalTimeParametersFrame> {
    constructor(data: GlobalTimeParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/device/off/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//FloatingTime
class GetFloatingTimeRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/device/floating/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetFloatingTimeRequest extends DataFrame<GlobalTimeParametersFrame> {
    constructor(data: GlobalTimeParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/device/floating/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//Turn on off time activation
class GetTurnOnOffTimeActivation extends DataFrame<string> {
    constructor(deviceGuid: string) {
        super();
        this.Resource = "/extalife/device/parameters/activation/onoff/times";
        this.Method = Method.Get;
        this.Data = deviceGuid;
    }
}

class SetTurnOnOffTimeActivation extends DataFrame<ActivationOnOffTimes> {
    constructor(data: ActivationOnOffTimes) {
        super();
        this.Resource = "/extalife/device/parameters/activation/onoff/times";
        this.Method = Method.Put;
        this.Data = data;
    }
}